import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { IonPage, IonContent } from '@ionic/react';
import { useStore } from "../store/useStore";
import { PageHeader } from "../components/ui/PageHeader";
import { PrimaryButton } from "../components/ui/Button";
import { RoomIcon } from "../components/ui/RoomIcon";
import { cn } from "../lib/utils";

const COMMON_AREAS = [
  "Kitchen",
  "Living Room",
  "Bathroom",
  "Master Bedroom",
  "Kids Room",
  "Guest Room",
  "Office",
  "Dining Room",
  "Garage",
  "Laundry Room",
  "Entryway",
  "Basement",
  "Attic",
  "Backyard",
  "Front Yard",
];

export default function CreateAreaPage() {
  const history = useHistory();
  
  // Form state
  const [areaName, setAreaName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Get data from Zustand store
  const currentHouseholdId = useStore(state => state.currentHouseholdId);
  const createArea = useStore(state => state.createArea);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!areaName.trim() || !currentHouseholdId) {
      setError("Area name is required");
      return;
    }

    setIsLoading(true);
    setError("");
    
    try {
      await createArea(areaName.trim());
      // Navigate back to household page after successful creation
      history.push("/household");
    } catch (error) {
      console.error("Error creating area:", error);
      setError("Failed to create area. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <IonPage>
      <PageHeader title="Create New Area" backHref="/household" />
      <IonContent>
        <div className="max-w-lg mx-auto px-4 pt-2 pb-28">
          <div className="flex items-start gap-3 rounded-2xl bg-surface p-4 mb-6">
            <RoomIcon name={areaName} size="md" />
            <div>
              <h2 className="text-foreground font-bold mb-1">What is an area?</h2>
              <p className="text-muted-foreground text-sm">
                Areas are spaces in your home that contain tasks. For example: Kitchen, Living Room, Master Bedroom, Kids Room, Garage, etc.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Area Name */}
            <div>
              <label htmlFor="area-name" className="field-label">
                Area Name
              </label>
              <input
                id="area-name"
                type="text"
                value={areaName}
                onChange={(e) => setAreaName(e.target.value)}
                placeholder="e.g., Kitchen, Living Room, Master Bedroom"
                className="input-field"
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? "area-name-error" : undefined}
                required
                autoFocus
              />
              {error && (
                <p id="area-name-error" role="alert" className="text-destructive text-sm font-medium mt-2">
                  {error}
                </p>
              )}
            </div>

            {/* Common Areas Suggestions */}
            <div>
              <p className="text-muted-foreground text-sm font-semibold mb-3">Common areas</p>
              <div className="flex flex-wrap gap-2">
                {COMMON_AREAS.map((suggestion) => {
                  const selected = areaName === suggestion;
                  return (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => setAreaName(suggestion)}
                      aria-pressed={selected}
                      className={cn(
                        "inline-flex items-center gap-2 min-h-[44px] pl-1.5 pr-3.5 rounded-full text-sm font-semibold border transition-colors",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        selected
                          ? "border-primary bg-accent text-accent-foreground"
                          : "border-border bg-card text-foreground hover:bg-surface"
                      )}
                    >
                      <RoomIcon name={suggestion} size="sm" className="w-8 h-8 rounded-full" />
                      {suggestion}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-2">
              <PrimaryButton
                type="submit"
                disabled={isLoading || !areaName.trim()}
              >
                {isLoading ? "Creating..." : "Create Area"}
              </PrimaryButton>
            </div>
          </form>
        </div>
      </IonContent>
    </IonPage>
  );
}
